import React, { useEffect, useState } from "react";
import { Box, Chip, Grid, IconButton, Stack, TextField, Typography } from "@mui/material";
import { Edit } from "@mui/icons-material";
import { useAuth } from "../../hooks/AuthProvider";
import AppTable from "../../common/app-table";
import AppDropdown from "../../common/app-dropdown";
import HeightButton from "../../common/height-button";
import ButtonOne from "../../common/normal-button";
import TableSearchInput from "../TableSearchInput";
import typography from "../../cssUtils/typography";
import { ColorsConstants } from "../../cssUtils/constants";
import { SIP_REMINDER_TRACKING_ENDPOINT } from "../../config/urls";
import SipRecordFormDialog from "./SipRecordFormDialog";
import { formatWithTzLabel } from "../../utils/dateUtils";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const REMINDER_STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Reminder-1", value: "reminder_1" },
  { label: "Reminder-2", value: "reminder_2" },
  { label: "Reminder-3", value: "reminder_3" },
  { label: "Reminder-4", value: "reminder_4" },
  { label: "Reminder-5", value: "reminder_5" },
];

const LAST_SIP_PAID_OPTIONS = [
  { label: "All", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

const CUSTOMER_RESPONSE_OPTIONS = [
  { label: "All", value: "" },
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

const REMINDER_STATUS_LABELS = {
  reminder_1: "Reminder-1",
  reminder_2: "Reminder-2",
  reminder_3: "Reminder-3",
  reminder_4: "Reminder-4",
  reminder_5: "Reminder-5",
};

const STATUS_CHIP_COLOR = {
  yes: "success",
  no: "error",
  pending: "default",
  delivered: "success",
  failed: "error",
  reminder_1: "info",
  reminder_2: "info",
  reminder_3: "info",
  reminder_4: "info",
  reminder_5: "info",
};

const FILTER_LABEL_SX = {
  ...typography.bodySmall,
  color: ColorsConstants.grayFontColor,
  fontWeight: 600,
  mb: 0.5,
  display: "block",
};

const renderStatusChip = (value, labelMap = {}) => {
  if (!value) return <Typography sx={typography.bodyLarge}>-</Typography>;
  return (
    <Chip
      size="small"
      variant="outlined"
      color={STATUS_CHIP_COLOR[value] || "default"}
      label={labelMap[value] || value}
    />
  );
};

const formatDateTime = (value) => (value ? formatWithTzLabel(value) : "-");

export default function SipTrackingScreen() {
  const { getValidToken } = useAuth();

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [controller, setController] = useState({ page: 0, rowsPerPage: 20 });
  const [resendingId, setResendingId] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [reminderStatus, setReminderStatus] = useState("");
  const [lastSipPaid, setLastSipPaid] = useState("");
  const [fundName, setFundName] = useState("");
  const [customerResponse, setCustomerResponse] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);

  const buildHeaders = async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const headers = await buildHeaders();
      const params = new URLSearchParams({
        limit: controller.rowsPerPage,
        offset: controller.page * controller.rowsPerPage,
      });
      if (searchQuery.trim()) params.set("search", searchQuery.trim());
      if (reminderStatus) params.set("reminder_status", reminderStatus);
      if (lastSipPaid) params.set("last_sip_paid", lastSipPaid);
      if (fundName.trim()) params.set("fund_name", fundName.trim());
      if (customerResponse) params.set("customer_response", customerResponse);

      const response = await fetch(`${SIP_REMINDER_TRACKING_ENDPOINT}?${params.toString()}`, {
        headers,
      });
      if (!response.ok) throw new Error("Failed to fetch SIP tracking records");
      const data = await response.json();
      setRows(data.results || []);
      setRowCount(data.count || 0);
    } catch (error) {
      console.error("Error fetching SIP tracking records:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(fetchRows, 350);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controller, searchQuery, fundName, reminderStatus, lastSipPaid, customerResponse]);

  const handleResend = async (row) => {
    setResendingId(row.id);
    try {
      const headers = await buildHeaders();
      const response = await fetch(`${SIP_REMINDER_TRACKING_ENDPOINT}${row.id}/resend/`, {
        method: "POST",
        headers,
      });
      if (!response.ok) throw new Error("Failed to resend reminder");
      await fetchRows();
    } catch (error) {
      console.error("Error resending SIP reminder:", error);
    } finally {
      setResendingId(null);
    }
  };

  const handleAdd = () => {
    setEditData(null);
    setIsModalOpen(true);
  };

  const handleEdit = (row) => {
    setEditData(row);
    setIsModalOpen(true);
  };

  const handleSave = async (form) => {
    const url = editData
      ? `${SIP_REMINDER_TRACKING_ENDPOINT}${editData.id}/`
      : SIP_REMINDER_TRACKING_ENDPOINT;
    const method = editData ? "PATCH" : "POST";

    setSaving(true);
    try {
      const headers = await buildHeaders();
      const response = await fetch(url, { method, headers, body: JSON.stringify(form) });
      if (!response.ok) throw new Error("Failed to save SIP record");
      setIsModalOpen(false);
      await fetchRows();
    } catch (error) {
      console.error("Error saving SIP record:", error);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { id: "customer_name", label: "Customer Name" },
    {
      id: "country_code",
      label: "Country Code",
      render: (row) => (
        <Typography sx={typography.bodyLarge}>+{row.country_code}</Typography>
      ),
    },
    { id: "phone_number", label: "Phone Number" },
    { id: "fund_name", label: "Fund Name" },
    {
      id: "sip_amount",
      label: "SIP Amount",
      align: "right",
      render: (row) => <Typography sx={typography.bodyLarge}>₹{row.sip_amount}</Typography>,
    },
    {
      id: "sip_frequency",
      label: "SIP Frequency",
      render: (row) => (
        <Typography sx={typography.bodyLarge}>
          {row.sip_frequency?.replace(/_/g, "-").replace(/\b\w/g, (c) => c.toUpperCase())}
        </Typography>
      ),
    },
    { id: "last_sip_payment_date", label: "Last SIP Payment Date" },
    { id: "next_sip_date", label: "Next SIP Date" },
    {
      id: "last_sip_paid",
      label: "Last SIP Paid",
      render: (row) => renderStatusChip(row.last_sip_paid),
    },
    {
      id: "reminder_status",
      label: "Reminder Status",
      render: (row) => renderStatusChip(row.reminder_status, REMINDER_STATUS_LABELS),
    },
    {
      id: "customer_response",
      label: "Customer Response",
      render: (row) => renderStatusChip(row.customer_response),
    },
    {
      id: "last_reminder_sent",
      label: "Last Reminder Sent",
      render: (row) => (
        <Typography sx={typography.bodyLarge}>{formatDateTime(row.last_reminder_sent)}</Typography>
      ),
    },
    {
      id: "notification_status",
      label: "Notification Status",
      render: (row) => renderStatusChip(row.notification_status),
    },
    {
      id: "actions",
      label: "Actions",
      align: "center",
      render: (row) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          <IconButton size="small" onClick={() => handleEdit(row)}>
            <Edit fontSize="small" />
          </IconButton>
          <HeightButton
            variant="outlined"
            disabled={resendingId === row.id}
            onClick={() => handleResend(row)}
          >
            {resendingId === row.id ? "Sending..." : "Resend"}
          </HeightButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={typography.h1}>SIP Reminder Tracking</Typography>
        <ButtonOne onClick={handleAdd}>+ Add SIP Record</ButtonOne>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-end">
        <Grid item xs={12} sm={4} md={3}>
          <Typography component="span" sx={FILTER_LABEL_SX}>
            Search (Customer Name / Phone)
          </Typography>
          <TableSearchInput
            searchQuery={searchQuery}
            onSearchChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by customer name or phone"
          />
        </Grid>
        <Grid item xs={6} sm={2}>
          <Typography component="span" sx={FILTER_LABEL_SX}>
            Reminder Status
          </Typography>
          <AppDropdown
            sx={{ width: "100%", justifyContent: "space-between" }}
            menuItems={REMINDER_STATUS_OPTIONS}
            selectedValue={reminderStatus}
            onMenuSelect={(value) => setReminderStatus(value)}
            endIcon={<ExpandMoreIcon />}
          >
            {REMINDER_STATUS_OPTIONS.find((o) => o.value === reminderStatus)?.label || "All"}
          </AppDropdown>
        </Grid>
        <Grid item xs={6} sm={2}>
          <Typography component="span" sx={FILTER_LABEL_SX}>
            Last SIP Paid
          </Typography>
          <AppDropdown
            sx={{ width: "100%", justifyContent: "space-between" }}
            menuItems={LAST_SIP_PAID_OPTIONS}
            selectedValue={lastSipPaid}
            onMenuSelect={(value) => setLastSipPaid(value)}
            endIcon={<ExpandMoreIcon />}
          >
            {LAST_SIP_PAID_OPTIONS.find((o) => o.value === lastSipPaid)?.label || "All"}
          </AppDropdown>
        </Grid>
        <Grid item xs={6} sm={2}>
          <Typography component="span" sx={FILTER_LABEL_SX}>
            Customer Response
          </Typography>
          <AppDropdown
            sx={{ width: "100%", justifyContent: "space-between" }}
            menuItems={CUSTOMER_RESPONSE_OPTIONS}
            selectedValue={customerResponse}
            onMenuSelect={(value) => setCustomerResponse(value)}
            endIcon={<ExpandMoreIcon />}
          >
            {CUSTOMER_RESPONSE_OPTIONS.find((o) => o.value === customerResponse)?.label || "All"}
          </AppDropdown>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Fund Name"
            placeholder="e.g. Axis Bluechip Fund"
            value={fundName}
            onChange={(e) => setFundName(e.target.value)}
          />
        </Grid>
      </Grid>

      <AppTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={loading}
        emptyMessage="No SIP records found"
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        onPageChange={(e, newPage) => setController((prev) => ({ ...prev, page: newPage }))}
        onRowsPerPageChange={(e) =>
          setController({ rowsPerPage: parseInt(e.target.value, 10), page: 0 })
        }
      />

      <SipRecordFormDialog
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        editData={editData}
        saving={saving}
      />
    </Box>
  );
}
